import {
  deleteDoc, doc, getDoc, runTransaction, serverTimestamp, setDoc,
} from 'firebase/firestore';
import { auth, db } from '../../services/firebase';

export const MAX_PREPARATION_DOCUMENTS = 3;

const requireIdentity = ({ childId, draftId }) => {
  if (!childId || !draftId) throw new Error('Choose a preparation first.');
  if (!auth.currentUser?.uid) throw new Error('Please sign in before updating this preparation.');
};

export const saveCustomPlanProposal = async ({ childId, draftId, proposal }) => {
  requireIdentity({ childId, draftId });
  const ref = doc(db, 'children', childId, 'preparationDrafts', draftId);
  const existing = await getDoc(ref);
  if (!existing.exists()) throw new Error('This preparation is no longer available.');
  if (existing.data()?.journeyPlan) return;
  await setDoc(ref, {
    customPlanProposal: proposal,
    updatedAt: serverTimestamp(),
    updatedBy: auth.currentUser.uid,
  }, { merge: true });
};

export const discardCustomPlanProposal = async ({ childId, draftId }) => {
  requireIdentity({ childId, draftId });
  const ref = doc(db, 'children', childId, 'preparationDrafts', draftId);
  const existing = await getDoc(ref);
  if (!existing.exists()) return;
  if (existing.data()?.journeyPlan) {
    throw new Error('A preparation that has already started cannot be discarded here.');
  }
  await deleteDoc(ref);
};

export const linkPreparationDocument = async ({ childId, draftId, sourceNoteId, label = '', status = 'reading' }) => {
  requireIdentity({ childId, draftId });
  if (!sourceNoteId) throw new Error('The uploaded document could not be linked.');
  const ref = doc(db, 'children', childId, 'preparationDrafts', draftId);
  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(ref);
    if (!snapshot.exists()) throw new Error('This preparation is no longer available.');
    const data = snapshot.data() || {};
    const documents = Array.isArray(data.preparationDocuments) ? data.preparationDocuments : [];
    if (documents.some((item) => item.sourceNoteId === sourceNoteId)) return documents;
    if (documents.length >= MAX_PREPARATION_DOCUMENTS) {
      throw new Error(`You can add up to ${MAX_PREPARATION_DOCUMENTS} documents to one preparation.`);
    }
    const next = [...documents, {
      sourceNoteId,
      label: String(label || 'Uploaded document').trim(),
      status,
      linkedAt: new Date().toISOString(),
    }];
    const update = {
      preparationDocuments: next,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser.uid,
    };
    if (data.journeyPlan) {
      update.preparationStatus = 'updating';
      update.backgroundRequestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      update.backgroundRequestedAt = serverTimestamp();
    }
    transaction.update(ref, update);
    return next;
  });
};

export const unlinkPreparationDocument = async ({ childId, draftId, sourceNoteId }) => {
  requireIdentity({ childId, draftId });
  const ref = doc(db, 'children', childId, 'preparationDrafts', draftId);
  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(ref);
    if (!snapshot.exists()) return [];
    const data = snapshot.data() || {};
    const next = (Array.isArray(data.preparationDocuments) ? data.preparationDocuments : [])
      .filter((item) => item.sourceNoteId !== sourceNoteId);
    transaction.update(ref, {
      preparationDocuments: next,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser.uid,
    });
    return next;
  });
};
